// ============================================
// Notification -> route mapping
// ============================================
//
// Bug #161: bấm vào một thông báo không đưa người dùng đi đâu cả.
//
// Bản trước của file này ĐOÁN đích đến ở phía FE: dò UUID trong metadata
// (`transaction_id`, `order_id`, `session_id`, ...), suy theo `type`, và cuối
// cùng còn quét chữ trong `title`/`body` để bắt các khoá i18n kiểu
// `notification.payment_succeeded.title`. Đoán thì bao giờ cũng có ca sai, và
// một payload thường kèm nhiều id cùng lúc nên rất dễ mở nhầm màn.
//
// Backend đã thay thế toàn bộ phần đoán đó. `GET /app/notifications/me/` nay
// trả thêm `reference_type`, `reference_id`, `deeplink`, TÍNH TỪ `metadata`
// ngay lúc đọc — nên bản ghi cũ cũng resolve được, không cần migration. Không
// suy được thì BE trả `null` cho cả ba.
//
// Nên ở đây chỉ còn đúng một việc: map `reference_type` -> route của
// store-admin. Lưu ý `type` (`GENERAL`/`SYSTEM`/`TRANSACTION`/...) là NHÓM GỬI,
// KHÔNG phải đích đến — đừng dùng nó để điều hướng.

import { ADMIN_ROUTES } from '@conai/shared-constants';

export interface NotificationLike {
  reference_type?: string | null;
  reference_id?: string | null;
  deeplink?: string | null;
}

/**
 * Đích đến của một `reference_type`.
 *
 * `null` = store-admin CHƯA có màn nào cho loại này (BE đã biết và chấp nhận).
 * Trả `null` để `TopNavBar` mở popup chi tiết tại chỗ, thay vì nhảy đại về
 * Dashboard — kéo người dùng ra khỏi chỗ họ đang làm còn tệ hơn không đi đâu.
 */
type RouteBuilder = ((id: string) => string) | null;

/**
 * Ghép query param, bỏ qua khi không có id — `?search=` rỗng chỉ làm bẩn URL.
 */
function withQuery(route: string, key: string, id: string): string {
  return id ? `${route}?${key}=${encodeURIComponent(id)}` : route;
}

/**
 * `/payments/:id` và `/orders/:id` CÓ trong `ADMIN_ROUTES` nhưng CHƯA đăng ký
 * trong `AppRouter` — điều hướng thẳng vào đó sẽ rơi vào trang 404. Nên tạm đưa
 * sang màn danh sách kèm `?search=<id>` (cả `TransactionsPage` lẫn `OrdersPage`
 * đều đọc `?search=`). Khi trang chi tiết được đăng ký thì đổi đúng hai dòng
 * dưới đây sang `buildPath(ADMIN_ROUTES.PAYMENTS_DETAIL, { id })`.
 */
const REFERENCE_ROUTES: Record<string, RouteBuilder> = {
  transaction: (id) => withQuery(ADMIN_ROUTES.PAYMENTS, 'search', id),
  order: (id) => withQuery(ADMIN_ROUTES.ORDERS, 'search', id),

  // `PromotionsPage` tự mở modal chi tiết khi thấy `?id=`.
  promotion: (id) => withQuery(ADMIN_ROUTES.PROMOTIONS, 'id', id),

  // Chưa có route chi tiết phiên mua (`ADMIN_ROUTES.SHOPPING_SESSION_DETAIL`
  // chưa đăng ký) -> về tab Shopping Sessions của Access Control, là màn gần
  // nhất bên admin theo dõi phiên mua.
  session: () => `${ADMIN_ROUTES.ACCESS_CONTROL}?view=sessions`,

  // Hàng đợi duyệt 18+ là một màn danh sách, không có chi tiết theo id.
  age_verification: () => ADMIN_ROUTES.AGE_VERIFICATIONS,

  // `ADMIN_ROUTES.STORES_DETAIL` cũng chưa đăng ký -> về danh sách cửa hàng.
  store: () => ADMIN_ROUTES.STORES,

  // Góp ý của khách (bug #208). Màn Feedback mở chi tiết bằng modal ngay trên
  // danh sách: KHÔNG có route theo id và cũng CHƯA đọc query param nào — nên
  // gắn `?id=<id>` vào lúc này chỉ làm bẩn URL chứ không mở đúng góp ý.
  // Vì vậy chỉ đưa về danh sách và bỏ qua `reference_id`.
  // Khi `FeedbackPage` biết đọc `?id=` để tự mở modal (giống `PromotionsPage`),
  // nâng cấp ĐÚNG MỘT CHỖ là dòng dưới đây:
  //   feedback: (id) => withQuery(ADMIN_ROUTES.FEEDBACK, 'id', id),
  feedback: () => ADMIN_ROUTES.FEEDBACK,

  /**
   * Cảnh báo lô cận hạn / quá hạn (mục #221, BE 18/09/2026).
   *
   * Job chạy hằng ngày, gửi MỘT notification mỗi cửa hàng và ghi đè lần trước
   * — 40 lô không thành 40 banner. `reference_id` là id CỬA HÀNG, không phải
   * id lô, nên đưa về danh sách lô của đúng cửa hàng đó thay vì mở một lô.
   */
  batch_expiry: (id) => withQuery(ADMIN_ROUTES.INVENTORY_BATCHES, 'store', id),

  // Các luồng chỉ tồn tại ở app khách — store-admin chưa có màn tương ứng.
  dispute: null,
  return: null,
  payment_method: null,
};

/**
 * Deeplink của BE có dạng `conai://<resource>/<id>` (hoặc `conai://<resource>`
 * với loại không có id, vd `conai://age-verification`). CỐ Ý không phải path
 * HTTP: user-app và store-admin mount cùng resource ở route khác nhau.
 *
 * Bảng này chỉ để dịch ngược `<resource>` về `reference_type`, dùng làm phương
 * án dự phòng khi bản BE trả `deeplink` mà thiếu `reference_type`.
 */
const DEEPLINK_RESOURCES: Record<string, string> = {
  payments: 'transaction',
  orders: 'order',
  sessions: 'session',
  disputes: 'dispute',
  returns: 'return',
  feedback: 'feedback',
  promotions: 'promotion',
  'payment-methods': 'payment_method',
  stores: 'store',
  'age-verification': 'age_verification',
  batches: 'batch_expiry',
};

const DEEPLINK_SCHEME = 'conai://';

interface Reference {
  type: string;
  id: string;
}

/** `conai://payments/8f3c...` -> `{ type: 'transaction', id: '8f3c...' }`. */
function parseDeeplink(deeplink: string): Reference | null {
  if (!deeplink.startsWith(DEEPLINK_SCHEME)) return null;

  const [resource, id = ''] = deeplink.slice(DEEPLINK_SCHEME.length).split('/');
  const type = DEEPLINK_RESOURCES[resource];

  return type ? { type, id } : null;
}

function readReference(noti: NotificationLike): Reference | null {
  const type = noti?.reference_type?.trim();
  if (type) return { type, id: noti?.reference_id?.trim() ?? '' };

  // Dự phòng cho trường hợp lệch bản: BE trả `deeplink` nhưng thiếu cặp
  // reference. Không bao giờ dùng khi `reference_type` đã có.
  const deeplink = noti?.deeplink?.trim();
  return deeplink ? parseDeeplink(deeplink) : null;
}

/**
 * Màn hình tương ứng của một thông báo, hoặc `null` khi không xác định được.
 *
 * Hàm thuần. `null` xảy ra ở ba trường hợp và UI xử lý giống nhau (mở popup chi
 * tiết):
 *  - BE không suy được đích đến (cả ba field đều `null`),
 *  - bản BE cũ chưa có ba field đó,
 *  - `reference_type` mà store-admin chưa có màn tương ứng.
 */
export function resolveNotificationRoute(noti: NotificationLike): string | null {
  const reference = readReference(noti);
  if (!reference) return null;

  const buildRoute = REFERENCE_ROUTES[reference.type];
  if (!buildRoute) return null;

  return buildRoute(reference.id);
}
